// src/techActivity.js
//
// Reusable client-side recorder for VISINVOKE Tech resource-use activity. The
// single entry point used by the Technician tab and every technician resource
// surface (downloads, Desktop Launcher, free utilities, registry files,
// diagnostics, repair tools, scripts, packages, documents).
//
// Fire-and-forget: recordTechActivity never throws, never blocks a download or
// launch, and silently ignores network or auth failures. It ALWAYS resolves, so
// callers never need try/catch around the underlying action. Server-side auth
// (require_auth) and all validation live in the backend; a non-technician user
// is safely recorded as a no-op there.
//
// No fetch and no browser storage APIs. Uses the shared cookie-auth axios
// instance, and the backend activity vocabulary only.

import API from './api';

// Canonical backend vocabulary (mirrors models.py). Do not invent new values.
export const TECH_ACTIVITY_EVENT_TYPES = [
  'DOWNLOAD_LINK_CLICKED', 'DESKTOP_LAUNCHER_ITEM_USED', 'UTILITY_USED',
  'REGISTRY_FILE_USED', 'DIAGNOSTIC_TOOL_USED', 'REPAIR_TOOL_USED',
  'SCRIPT_USED', 'PACKAGE_USED', 'DOCUMENT_OPENED', 'OTHER_TECH_ACTION',
];

export const TECH_ACTIVITY_ITEM_TYPES = [
  'download', 'desktop_launcher_tool', 'utility', 'registry_file',
  'diagnostic', 'repair_tool', 'script', 'package', 'document', 'other',
];

export const TECH_ACTIVITY_SOURCES = [
  'desktop_launcher', 'tech_utilities_page', 'free_utilities_page',
  'technician_dashboard', 'download_page', 'registry_files_page',
  'diagnostics_page', 'repair_tools_page', 'script_page', 'package_page',
  'other',
];

function inList(value, list) {
  return typeof value === 'string' && list.indexOf(value) !== -1;
}

// Record a single technician activity event. Fire-and-forget.
//
// payload:
//   event_type   (required) one of TECH_ACTIVITY_EVENT_TYPES; an unknown value
//                is coerced to 'OTHER_TECH_ACTION' so the action is still logged
//   item_type    (optional) one of TECH_ACTIVITY_ITEM_TYPES; unknown is omitted
//   source       (optional) one of TECH_ACTIVITY_SOURCES; unknown is omitted
//   item_slug    (optional) string
//   item_name    (optional) string
//   item_version (optional) string
//   source_page  (optional) string
//   status       (optional) string, backend defaults to 'success'
//   metadata     (optional) plain object
//
// Returns a Promise that ALWAYS resolves: true when the POST succeeded, false
// otherwise (missing event, network error, auth failure). Never rejects.
export function recordTechActivity(payload) {
  try {
    const p = payload || {};

    let eventType = typeof p.event_type === 'string' ? p.event_type.trim() : '';
    if (!eventType) {
      // Nothing meaningful to record; do not send an empty event.
      return Promise.resolve(false);
    }
    if (!inList(eventType, TECH_ACTIVITY_EVENT_TYPES)) {
      eventType = 'OTHER_TECH_ACTION';
    }

    const body = { event_type: eventType };

    // Controlled-vocabulary fields: include only when valid, else omit (so a
    // caller typo can never cause the backend to reject the whole event).
    if (inList(p.item_type, TECH_ACTIVITY_ITEM_TYPES)) body.item_type = p.item_type;
    if (inList(p.source, TECH_ACTIVITY_SOURCES))       body.source    = p.source;

    // Free-text fields.
    if (p.item_slug)    body.item_slug    = String(p.item_slug);
    if (p.item_name)    body.item_name    = String(p.item_name);
    if (p.item_version) body.item_version = String(p.item_version);
    if (p.source_page)  body.source_page  = String(p.source_page);
    if (p.status)       body.status       = String(p.status);
    if (p.metadata && typeof p.metadata === 'object') body.metadata = p.metadata;

    return API.post('/v1/technician-application/activity', body)
      .then(function () { return true; })
      .catch(function () { return false; });
  } catch (e) {
    // Never let recording interfere with the caller's action.
    return Promise.resolve(false);
  }
}

export default recordTechActivity;
